import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Users, CheckCircle, XCircle, ClipboardCheck, Clock, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarkTeamAttendanceSection = () => {
  const [loading, setLoading] = useState(true);
  const [topics, setTopics] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAttendanceTopics();
  }, []);

  const fetchAttendanceTopics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training/attendance/topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopics(response.data.topics || []);
      setDebugInfo(response.data.debug_info || null);
    } catch (error) {
      console.error('Failed to fetch attendance chapters:', error);
      toast.error('Failed to load attendance chapters');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicSelect = async (topic) => {
    setSelectedTopic(topic);
    setTeamMembers(topic.team_members || []);
    
    // Initialize attendance status for each team member
    const initialStatus = {};
    (topic.team_members || []).forEach(member => {
      initialStatus[member.staff_id] = member.attendance_status || 'pending';
    });
    setAttendanceStatus(initialStatus);
  };

  const handleMarkAttendance = async (staffId, status) => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/training/attendance/mark`, {
        topic_id: selectedTopic.id,
        staff_id: staffId,
        is_present: status === 'present'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setAttendanceStatus(prev => ({
        ...prev,
        [staffId]: status
      }));

      // Update team members list to reflect the change
      setTeamMembers(prev => prev.map(member => 
        member.staff_id === staffId 
          ? { ...member, attendance_status: status }
          : member
      ));

      toast.success(`Attendance marked as ${status === 'present' ? 'Present' : 'Absent'}`);
    } catch (error) {
      console.error('Failed to mark attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkMarkAll = async (isPresent) => {
    const pendingMembers = teamMembers.filter(m => attendanceStatus[m.staff_id] === 'pending');
    if (pendingMembers.length === 0) {
      toast.info('No pending attendance to mark');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/training/attendance/bulk-mark`, {
        topic_id: selectedTopic.id,
        attendance_records: pendingMembers.map(m => ({
          staff_id: m.staff_id,
          is_present: isPresent
        }))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      const newStatus = { ...attendanceStatus };
      pendingMembers.forEach(m => {
        newStatus[m.staff_id] = isPresent ? 'present' : 'absent';
      });
      setAttendanceStatus(newStatus);

      // Update team members list
      setTeamMembers(prev => prev.map(member => ({
        ...member,
        attendance_status: pendingMembers.find(m => m.staff_id === member.staff_id)
          ? (isPresent ? 'present' : 'absent')
          : member.attendance_status
      })));

      toast.success(`Marked ${pendingMembers.length} members as ${isPresent ? 'Present' : 'Absent'}`);
    } catch (error) {
      console.error('Failed to bulk mark attendance:', error);
      toast.error('Failed to mark attendance');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#0056B3] animate-spin" />
        <span className="ml-3 text-gray-600">Loading attendance chapters...</span>
      </div>
    );
  }

  if (topics.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <ClipboardCheck className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Attendance Chapters</h3>
        <p className="text-gray-600 mb-4">There are no offline or hybrid training chapters requiring attendance marking.</p>
        
        {debugInfo && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg max-w-md mx-auto text-left">
            <p className="text-sm font-medium text-gray-700 mb-2">Diagnostic Information:</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Team members under you: <strong>{debugInfo.team_members_count || 0}</strong></li>
              <li>• Chapters assigned via job roles: <strong>{debugInfo.total_topics_from_job_functions || 0}</strong></li>
              <li>• Offline/Hybrid chapters found: <strong>{debugInfo.offline_hybrid_topics_found || 0}</strong></li>
            </ul>
            {debugInfo.team_members_count === 0 && (
              <p className="mt-3 text-xs text-amber-600">
                ⚠️ You have no team members assigned. Staff must be assigned to you as their reporting manager.
              </p>
            )}
            {debugInfo.team_members_count > 0 && debugInfo.offline_hybrid_topics_found === 0 && (
              <p className="mt-3 text-xs text-amber-600">
                ⚠️ No offline or hybrid training chapters exist. Chapters must be created with "Offline" or "Hybrid" training type.
              </p>
            )}
          </div>
        )}
        
        <Button 
          variant="outline" 
          size="sm" 
          onClick={fetchAttendanceTopics}
          className="mt-4 border-[#0056B3] text-[#0056B3]"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Mark Team Attendance</h2>
          <p className="text-sm text-gray-600 mt-1">
            Mark attendance for your team members on offline and hybrid training chapters
          </p>
        </div>
      </div>

      {/* Topics Selection */}
      {!selectedTopic ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {topics.map((topic) => (
            <Card
              key={topic.id}
              className="p-4 cursor-pointer hover:shadow-md transition-shadow border-2 hover:border-[#0056B3]"
              onClick={() => handleTopicSelect(topic)}
            >
              <div className="flex items-start justify-between mb-3">
                <Badge className={
                  topic.training_type === 'offline' 
                    ? 'bg-amber-100 text-amber-800' 
                    : 'bg-purple-100 text-purple-800'
                }>
                  {topic.training_type === 'offline' ? 'Offline' : 'Hybrid'}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {topic.pending_count || 0} pending
                </Badge>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{topic.title}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {topic.total_members || 0} members
                </span>
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {topic.present_count || 0}
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <XCircle className="w-4 h-4" />
                  {topic.absent_count || 0}
                </span>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        /* Team Members Attendance View */
        <div>
          {/* Back button and topic info */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-semibold text-gray-900">{selectedTopic.title}</h3>
              <Badge className={
                selectedTopic.training_type === 'offline' 
                  ? 'bg-amber-100 text-amber-800' 
                  : 'bg-purple-100 text-purple-800'
              }>
                {selectedTopic.training_type === 'offline' ? 'Offline Training' : 'Hybrid Training'}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedTopic(null);
                setTeamMembers([]);
                setAttendanceStatus({});
              }}
            >
              ← Back to Chapters
            </Button>
          </div>

          {/* Bulk Actions */}
          <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">
                {teamMembers.filter(m => attendanceStatus[m.staff_id] === 'pending').length}
              </span> members pending attendance
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkMarkAll(true)}
                disabled={submitting}
                className="text-green-600 border-green-600 hover:bg-green-50"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark All Present
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleBulkMarkAll(false)}
                disabled={submitting}
                className="text-red-600 border-red-600 hover:bg-red-50"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Mark All Absent
              </Button>
            </div>
          </div>

          {/* Team Members List */}
          <div className="space-y-3">
            {teamMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No team members assigned to this chapter
              </div>
            ) : (
              teamMembers.map((member) => {
                const status = attendanceStatus[member.staff_id] || 'pending';
                return (
                  <Card key={member.staff_id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#0056B3] flex items-center justify-center text-white font-semibold">
                          {member.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Status Badge */}
                        {status === 'present' && (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Present
                          </Badge>
                        )}
                        {status === 'absent' && (
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Absent
                          </Badge>
                        )}
                        {status === 'pending' && (
                          <Badge className="bg-yellow-100 text-yellow-800">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={status === 'present' ? 'default' : 'outline'}
                            onClick={() => handleMarkAttendance(member.staff_id, 'present')}
                            disabled={submitting || status === 'present'}
                            className={status === 'present' 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'text-green-600 border-green-600 hover:bg-green-50'
                            }
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant={status === 'absent' ? 'default' : 'outline'}
                            onClick={() => handleMarkAttendance(member.staff_id, 'absent')}
                            disabled={submitting || status === 'absent'}
                            className={status === 'absent' 
                              ? 'bg-red-600 hover:bg-red-700' 
                              : 'text-red-600 border-red-600 hover:bg-red-50'
                            }
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MarkTeamAttendanceSection;
